import { Input, InputHelperText } from '@picsio/ui';
import React, {
  forwardRef, useCallback, useEffect, useMemo, useState, memo, useRef,
} from 'react';
import { func } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import l18n from '../../shared/strings';
import Logger from '../../services/Logger';
import {
  isUploadStarted,
  requiredFields as requiredFieldsSelector,
} from '../../store/import/selectors';
import DetailsBlock from '../DetailsBlock';
import { getIndicator, panelsVisibilityShape } from './helpers';
import { DIFFERENT_VALUES } from '../../store/import/helpers/groupFields';

/** @NOTE: Used require just for conditional import */
const { changeField } = window.inbox
  ? require('../../store/inboxApp/actions')
  : require('../../store/import/actions');

const Comment = forwardRef(({ panelsVisibility, toggleVisibility }, ref) => {
  const $textarea = useRef();
  const dispatch = useDispatch();
  const numberOfSelectedFiles = useSelector((state) => state.import.selected.length);
  const isCommentsAllowed = useSelector((state) => Boolean(window.inbox ? true : state.user.subscriptionFeatures?.comments));
  const requiredFields = useSelector(requiredFieldsSelector);
  const storeValue = useSelector((state) => state.import.fields.comment);
  const error = useSelector((state) => state.import.errors.comment);
  const disabled = useSelector(isUploadStarted);

  const [value, setValue] = useState(storeValue || '');

  /** update value in the state when updated in the store */
  useEffect(() => {
    setValue(storeValue || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeValue]);

  const indicator = useMemo(() => getIndicator('comments', requiredFields), [requiredFields]);

  const handleChange = useCallback(({ target }) => {
    setValue(target.value);
  }, [setValue]);

  const handleBlur = useCallback(() => {
    if (value === DIFFERENT_VALUES) return;
    dispatch(changeField({ title: 'comment', value: value.trim() || null }));
    Logger.log('User', 'UploadPanelChangeComment', { assets_number: numberOfSelectedFiles });
  }, [value, dispatch, numberOfSelectedFiles]);

  const placeholder = value === DIFFERENT_VALUES
    ? l18n.DETAILS.placeholderMultipleSelection
    : l18n.IMPORT.placeholderInputComment;

  return (
    <DetailsBlock
      dataQa="comment"
      ref={ref}
      detailsPanelVisibility={panelsVisibility}
      toggleVisibility={toggleVisibility}
      blockName="importCommentVisibility"
      blockTitle={l18n.IMPORT.commentsTitle(isCommentsAllowed)}
      indicator={indicator}
      error={Boolean(error)}
      errorHighlight
      openByDefault={error || indicator !== null}
    >
      <div className="importFieldItem">
        <Input
          component="textarea"
          inputRef={$textarea}
          multiline
          autoResize
          minHeight={68}
          maxHeight={148}
          value={value === DIFFERENT_VALUES ? '' : value}
          error={Boolean(error)}
          placeholder={placeholder}
          onBlur={handleBlur}
          onChange={handleChange}
          disabled={disabled || !isCommentsAllowed}
        />
        <If condition={error}>
          <InputHelperText error>{error}</InputHelperText>
        </If>
      </div>
    </DetailsBlock>
  );
});

Comment.propTypes = {
  panelsVisibility: panelsVisibilityShape.isRequired,
  toggleVisibility: func.isRequired,
};

export default memo(Comment);
